import { FC } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { Dropdown } from '@carbon/react';
import { useAtom, useAtomValue } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { format2 } from 'utils/number';
import { limitAtom, allocationManagerChecksLoadableAtom } from './atoms';
import type { BrokenLineItem } from './types';
import styles from './page.module.scss';

interface LimitOption {
  id: string;
  label: string;
}

const LIMIT_OPTIONS: LimitOption[] = [
  { id: '100', label: '100 rows' },
  { id: '250', label: '250 rows' },
  { id: '500', label: '500 rows' },
  { id: '1000', label: '1000 rows' },
];

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<BrokenLineItem>();

const columns = [
  columnHelper.accessor('wellUwi', {
    header: 'Well UWI',
    size: 130,
  }),
  columnHelper.accessor('unitName', {
    header: 'Unit',
    size: 150,
  }),
  columnHelper.accessor('uatName', {
    header: 'UAT',
    size: 150,
  }),
  columnHelper.accessor('uatAcreage', {
    header: 'UAT Acreage',
    size: 120,
    cell: (info) => format2(info.getValue()),
  }),
  columnHelper.accessor('uatLateral', {
    header: 'UAT Lateral (ft)',
    size: 140,
    cell: (info) => format2(info.getValue(), 'NULL'),
  }),
  columnHelper.accessor('totalLateralLength', {
    header: 'Total Lateral (ft)',
    size: 150,
    cell: (info) => format2(info.getValue()),
  }),
  columnHelper.accessor('allocationManagerId', {
    header: 'AM ID',
    size: 80,
  }),
  columnHelper.accessor('uatId', {
    header: 'UAT ID',
    size: 80,
  }),
];

export const AllocationManagerChecksPage: FC = () => {
  const [limit, setLimit] = useAtom(limitAtom);
  const loadable = useAtomValue(allocationManagerChecksLoadableAtom);

  const isLoading = loadable.state === 'loading';
  const data = loadable.state === 'hasData' ? loadable.data.results : [];
  const stats = loadable.state === 'hasData' ? loadable.data.stats : null;

  const limitDropdown = (
    <Dropdown
      id="limit-dropdown"
      label="Rows"
      titleText=""
      items={LIMIT_OPTIONS}
      itemToString={(item: LimitOption) => item?.label ?? ''}
      selectedItem={LIMIT_OPTIONS.find((o) => o.id === String(limit))}
      onChange={({ selectedItem }: { selectedItem: LimitOption }) =>
        setLimit(Number(selectedItem.id))
      }
      size="sm"
    />
  );

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Allocation Manager Checks"
        actions={limitDropdown}
      />

      <div className={styles.sectionHeader}>
        <p>
          Allocation managers using <code>lateral_length</code> method where one
          or more line items have a <code>NULL</code>{' '}
          <code>unit_allocation_tract_lateral_value</code>. These wells cannot
          have NRI calculated because the per-UAT lateral allocation is missing.
        </p>
        <p className={styles.sectionDescription}>
          The lateral value is populated from the ArcGIS{' '}
          <code>Allocation_Manager_Line_Item</code> feature layer. A NULL value
          means the GIS data has not been published for this UAT/well
          combination.
        </p>
      </div>

      {stats && (
        <StatsBar
          stats={[
            {
              label: 'Broken Line Items',
              value: stats.totalLineItems,
              tagType: 'red',
            },
            {
              label: 'Affected Wells',
              value: stats.affectedWells,
              tagType: 'magenta',
            },
            {
              label: 'Affected Units',
              value: stats.affectedUnits,
              tagType: 'teal',
            },
            {
              label: 'Affected AMs',
              value: stats.affectedAllocationManagers,
              tagType: 'purple',
            },
          ]}
        />
      )}

      <h3>Lateral Length UATs with NULL UAT Lateral Length</h3>
      <dl className={styles.fieldDescriptions}>
        <dt className={styles.fieldDescriptionsDt}>Well UWI</dt>
        <dd className={styles.fieldDescriptionsDd}>
          The well identifier with a lateral_length allocation manager.
        </dd>
        <dt className={styles.fieldDescriptionsDt}>Unit</dt>
        <dd className={styles.fieldDescriptionsDd}>
          The development area (unit) the well is assigned to.
        </dd>
        <dt className={styles.fieldDescriptionsDt}>UAT</dt>
        <dd className={styles.fieldDescriptionsDd}>
          The Unit Allocation Tract referenced by the broken line item.
        </dd>
        <dt className={styles.fieldDescriptionsDt}>UAT Acreage</dt>
        <dd className={styles.fieldDescriptionsDd}>
          The acreage allocation value for this UAT (may be populated even when
          lateral is missing).
        </dd>
        <dt className={styles.fieldDescriptionsDt}>UAT Lateral (ft)</dt>
        <dd className={styles.fieldDescriptionsDd}>
          The lateral length allocation value &mdash; this is the missing field.
          Shows <code className={styles.fieldDescriptionsCode}>NULL</code> for
          broken records.
        </dd>
        <dt className={styles.fieldDescriptionsDt}>Total Lateral (ft)</dt>
        <dd className={styles.fieldDescriptionsDd}>
          The well&apos;s total lateral length from the allocation manager.
        </dd>
      </dl>

      <DataTable
        data={data}
        columns={columns as ColumnDef<BrokenLineItem>[]}
        getRowId={(row) => `${row.allocationManagerId}-${row.uatId}`}
        isLoading={isLoading}
        {...TABLE_CONFIG}
      />
    </>
  );
};
